import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  MultipleChoiceSetupState,
  MultipleChoiceGameState,
  MultipleChoiceQuestion,
  generateQuestions,
  initializeGameState,
  handleCorrectAnswer,
  handleWrongAnswer,
  handleSkip,
  getCurrentQuestion,
  calculateGameStats,
  saveWrongAnswerLocally,
  LANGUAGE_CONFIG,
  LEVEL_CONFIG
} from '../utils/multipleChoiceLogic';
import { getVocabularyItems } from '../data/vocabulary';
import MultipleChoiceResults from './MultipleChoiceResults';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import '../styles/MultipleChoiceGame.css';

interface MultipleChoiceGameProps {
  setup: MultipleChoiceSetupState;
  onExit: () => void;
  onPlayAgain: () => void;
}

const MultipleChoiceGame: React.FC<MultipleChoiceGameProps> = ({ setup, onExit, onPlayAgain }) => {
  const { user, isAuthenticated } = useAuth();
  const [gameState, setGameState] = useState<MultipleChoiceGameState | null>(null);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [disabledOptions, setDisabledOptions] = useState<Set<string>>(new Set());

  // Track wrong answers to save at end of game (for logged-in users)
  const wrongAnswersToSave = useRef<MultipleChoiceQuestion[]>([]);

  // Initialize game on mount
  useEffect(() => {
    if (!setup.language || !setup.level) return;

    // Get vocabulary items for the selected level
    const levelItems = getVocabularyItems(setup.language, setup.level);

    // Generate questions
    const questions = generateQuestions(
      levelItems,
      setup.selectedTypes,
      setup.questionCount
    );

    // Initialize game state
    const newGameState = initializeGameState(
      setup.language,
      setup.level,
      setup.selectedTypes,
      questions
    );

    setGameState(newGameState);
    wrongAnswersToSave.current = [];
  }, [setup]);

  // Save wrong answers when game completes
  useEffect(() => {
    if (gameState?.isComplete && wrongAnswersToSave.current.length > 0) {
      saveWrongAnswers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState?.isComplete]);

  // Save wrong answers to API (for logged-in users) or localStorage (for guests)
  const saveWrongAnswers = async () => {
    if (!setup.language || !setup.level) return;

    const wrongQuestions = wrongAnswersToSave.current;
    if (wrongQuestions.length === 0) return;

    if (isAuthenticated && user) {
      // Save to API for logged-in users
      try {
        const wrongAnswersPayload = wrongQuestions.map(q => ({
          vocabularyItemId: q.vocabularyItem.id,
          vocabularyData: q.vocabularyItem,
          language: setup.language,
          level: setup.level,
          questionType: q.questionType,
          wrongAttempts: q.wrongAttempts,
          userWrongAnswers: q.userWrongAnswers
        }));

        await apiService.post('/wrong-answers/bulk', { wrongAnswers: wrongAnswersPayload });
        console.log('Wrong answers saved to server');
      } catch (error) {
        console.error('Failed to save wrong answers to server:', error);
        // Fall back to localStorage
        wrongQuestions.forEach(q => {
          saveWrongAnswerLocally(q, setup.language!, setup.level!);
        });
      }
    } else {
      // Save to localStorage for guests
      wrongQuestions.forEach(q => {
        saveWrongAnswerLocally(q, setup.language!, setup.level!);
      });
    }
  };

  // Handle option click
  const handleOptionClick = useCallback((option: string) => {
    if (!gameState || showFeedback || disabledOptions.has(option)) return;

    const currentQuestion = getCurrentQuestion(gameState);
    if (!currentQuestion) return;

    setSelectedOption(option);
    setShowFeedback(true);

    if (option === currentQuestion.correctAnswer) {
      // Correct answer
      setIsCorrect(true);

      // Delay before moving to next question
      setTimeout(() => {
        setGameState(prev => prev ? handleCorrectAnswer(prev) : null);
        setSelectedOption(null);
        setShowFeedback(false);
        setIsCorrect(false);
        setDisabledOptions(new Set());
      }, 800);
    } else {
      // Wrong answer
      setIsCorrect(false);

      // Track this wrong answer for later saving
      const updatedQuestion = {
        ...currentQuestion,
        wasAnsweredWrong: true,
        wrongAttempts: currentQuestion.wrongAttempts + 1,
        userWrongAnswers: [...currentQuestion.userWrongAnswers, option]
      };

      // Check if this question is already tracked
      const existingIndex = wrongAnswersToSave.current.findIndex(
        q => q.vocabularyItem.id === currentQuestion.vocabularyItem.id && q.questionType === currentQuestion.questionType
      );

      if (existingIndex >= 0) {
        // Update existing
        wrongAnswersToSave.current[existingIndex] = updatedQuestion;
      } else {
        // Add new
        wrongAnswersToSave.current.push(updatedQuestion);
      }

      // Update game state and disable this wrong option
      setGameState(prev => prev ? handleWrongAnswer(prev, option) : null);
      setDisabledOptions(prev => new Set([...Array.from(prev), option]));

      // Reset feedback after delay but stay on question
      setTimeout(() => {
        setSelectedOption(null);
        setShowFeedback(false);
      }, 600);
    }
  }, [gameState, showFeedback, disabledOptions]);

  // Handle skip
  const handleSkipClick = useCallback(() => {
    if (!gameState || showFeedback) return;

    setGameState(prev => prev ? handleSkip(prev) : null);
    setSelectedOption(null);
    setDisabledOptions(new Set());
  }, [gameState, showFeedback]);

  // Show loading while initializing
  if (!gameState) {
    return (
      <div className="multiple-choice-game loading">
        <div className="loading-spinner"></div>
        <p>Loading questions...</p>
      </div>
    );
  }

  // Show results if game is complete
  if (gameState.isComplete) {
    const stats = calculateGameStats(gameState);
    return (
      <MultipleChoiceResults
        stats={stats}
        language={setup.language!}
        level={setup.level!}
        onPlayAgain={onPlayAgain}
        onExit={onExit}
      />
    );
  }

  const currentQuestion = getCurrentQuestion(gameState);
  if (!currentQuestion) {
    return <div className="multiple-choice-game error">No question available</div>;
  }

  const progress = ((gameState.currentQuestionIndex + 1) / gameState.questions.length) * 100;

  return (
    <div className="multiple-choice-game">
      {/* Header */}
      <div className="game-header">
        <button className="exit-button" onClick={onExit}>
          ✕ Exit
        </button>
        <div className="game-info">
          <span className="language-badge">
            {LANGUAGE_CONFIG[setup.language!].flag} {LANGUAGE_CONFIG[setup.language!].name}
          </span>
          <span className="level-badge">
            {LEVEL_CONFIG[setup.level!].icon} {LEVEL_CONFIG[setup.level!].name}
          </span>
        </div>
        <div className="score-display">
          Score: <span className={gameState.score >= 0 ? 'positive' : 'negative'}>{gameState.score}</span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="progress-container">
        <div className="progress-bar" style={{ width: `${progress}%` }}></div>
        <span className="progress-text">
          {gameState.currentQuestionIndex + 1} / {gameState.questions.length}
        </span>
      </div>

      {/* Question Card */}
      <div className="question-card">
        <div className="question-type-badge">
          {currentQuestion.questionType.replace(/-/g, ' → ').replace('to', '')}
        </div>

        <div className="question-prompt">
          {currentQuestion.prompt}
        </div>

        {currentQuestion.promptHint && (
          <div className="question-hint">
            {currentQuestion.promptHint}
          </div>
        )}
      </div>

      {/* Options */}
      <div className="options-container">
        {currentQuestion.options.map((option, index) => {
          const isSelected = selectedOption === option;
          const isDisabled = disabledOptions.has(option);
          const isCorrectOption = option === currentQuestion.correctAnswer;

          let optionClass = 'option-button';
          if (isDisabled) optionClass += ' disabled';
          if (showFeedback && isSelected) {
            optionClass += isCorrect ? ' correct' : ' wrong';
          }
          if (showFeedback && isCorrect && isCorrectOption) {
            optionClass += ' correct';
          }

          return (
            <button
              key={index}
              className={optionClass}
              onClick={() => handleOptionClick(option)}
              disabled={isDisabled || (showFeedback && isCorrect)}
            >
              <span className="option-letter">{String.fromCharCode(65 + index)}</span>
              <span className="option-text">{option}</span>
            </button>
          );
        })}
      </div>

      {/* Skip Button */}
      <div className="action-buttons">
        <button
          className="skip-button"
          onClick={handleSkipClick}
          disabled={showFeedback}
        >
          Skip (0 pts)
        </button>
      </div>

      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stat">
          <span className="stat-label">Correct</span>
          <span className="stat-value correct">{gameState.correctAnswers}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Wrong</span>
          <span className="stat-value wrong">{gameState.wrongAnswers}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Skipped</span>
          <span className="stat-value skipped">{gameState.skippedCount}</span>
        </div>
      </div>
    </div>
  );
};

export default MultipleChoiceGame;
